import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useTrimmedElementCheck } from 'shared/lib/utils';
import { SectionTitle } from 'shared/ui/section-title';
import { SectionInfo } from 'shared/ui/section-info';
import { SeeAllButton, SEE_ALL_ROUTES } from 'shared/ui/buttons';
import { Tooltip } from 'shared/ui/tooltip';
import { useRecommendations } from 'widgets/recommendations/model';
// eslint-disable-next-line boundaries/element-types
import { useStreamContentData } from 'widgets/stream-preview/lib';
import { useIsWalkthroughActive } from 'features/walkthrough';
import { useVideoModalState, VideoModal } from 'features/video-modal';
import {
  LiteracyInsightModal,
  useLiteracyInsightModalStateSelector,
} from 'features/literacy-insight-modal';
import { useClasses } from 'entities/classes';

import { RECOMMENDATIONS_PREVIEW_CAROUSEL_ID } from '../../constants';

import { RecommendationsPreviewContent } from './recommendations-preview-content';
import styles from './styles.module.scss';

const DEFAULT_SEE_ALL_RECOMMENDATIONS_COUNT = 3;
const WT_PREVIEW_COUNT = 1;

export const RecommendationsPreview = () => {
  const { t } = useTranslation();

  const [previewCount, setPreviewCount] = useState(
    DEFAULT_SEE_ALL_RECOMMENDATIONS_COUNT,
  );
  const [shouldShowCarousel, setShouldShowCarousel] = useState<boolean | null>(
    null,
  );

  const {
    itemsToDisplay,
    selectedClass,
    isLoading: isStreamLoading,
  } = useStreamContentData(false);

  const { classes } = useClasses();

  const { recommendations, isLoading: isRecommendationsLoading } =
    useRecommendations();

  const { isVideoModalVisible, videoInfo, hideVideoModal } =
    useVideoModalState();

  const { isLiteracyInsightModalVisible, literacyInsightModalInfo } =
    useLiteracyInsightModalStateSelector();

  const titleWrapperRef = useRef<HTMLDivElement>(null);
  const isTitleTrimmed = useTrimmedElementCheck(titleWrapperRef);

  const location = useLocation();
  const previousLocationUrl =
    location.state?.previousLocationPathname?.split('/')[1] ?? '';

  const hasNavigatedFromSeeAllPages =
    Object.values(SEE_ALL_ROUTES).includes(previousLocationUrl);

  const hasStreamQueriesInitiated = !classes?.length || itemsToDisplay !== null;

  const hasCachedStreamItems =
    hasNavigatedFromSeeAllPages || hasStreamQueriesInitiated;

  const isLoading =
    isRecommendationsLoading || isStreamLoading || !hasCachedStreamItems;

  const isWalkthroughActive = useIsWalkthroughActive();

  useEffect(() => {
    if (isLoading) return;

    if (!selectedClass.length || !itemsToDisplay?.length) {
      setShouldShowCarousel(false);
      return;
    }

    setShouldShowCarousel(true);
  }, [isLoading, itemsToDisplay?.length, selectedClass.length]);

  useEffect(() => {
    if (isLoading) return;

    if (isWalkthroughActive && !shouldShowCarousel) {
      setPreviewCount(WT_PREVIEW_COUNT);
    }

    if (!isWalkthroughActive) {
      setPreviewCount(DEFAULT_SEE_ALL_RECOMMENDATIONS_COUNT);
    }
  }, [isLoading, isWalkthroughActive, shouldShowCarousel]);

  const shouldShowSeeAll =
    recommendations.length > DEFAULT_SEE_ALL_RECOMMENDATIONS_COUNT;

  const previewRecommendations = recommendations.slice(0, previewCount);

  return (
    <div id="recommendations_section">
      <div
        className={clsx(
          'd-flex flex-column flex-md-row align-items-center justify-content-between',
          styles.recommendationsPreviewHeader,
        )}
      >
        <div className="d-flex justify-content-center justify-content-md-start align-items-center mb-2 mb-md-0 w-100">
          <Tooltip title={t('Recommendations')} suppress={!isTitleTrimmed}>
            <div
              className={styles.recommendationsPreviewTitleWrapper}
              tabIndex={isTitleTrimmed ? 0 : -1}
            >
              <SectionTitle
                classNames={clsx('my-0', styles.recommendationsPreviewTitle)}
                elementRef={titleWrapperRef}
                shouldRenderAsH1Heading
              >
                {t('Recommendations')}
              </SectionTitle>
            </div>
          </Tooltip>
          <SectionInfo
            messageText={t(
              'Here is where you will find suggested next steps, reminders, and notifications about helpful resources.',
            )}
            sectionName="recommendations"
          />
        </div>
        <div className="d-flex align-items-center flex-column flex-sm-row">
          <div
            id={RECOMMENDATIONS_PREVIEW_CAROUSEL_ID}
            className={clsx({
              [styles.recommendationsPreviewCarouselContainer]:
                shouldShowSeeAll,
            })}
          />
          {shouldShowSeeAll && (
            <SeeAllButton
              navigateTo={SEE_ALL_ROUTES.RECOMMENDATIONS}
              sectionName={SEE_ALL_ROUTES.RECOMMENDATIONS}
            />
          )}
        </div>
      </div>
      <RecommendationsPreviewContent
        recommendations={previewRecommendations}
        isLoading={isLoading || shouldShowCarousel === null}
        withCarousel={!!shouldShowCarousel}
      />
      {isVideoModalVisible && !!videoInfo && (
        <VideoModal onClose={hideVideoModal} {...videoInfo} />
      )}
      {isLiteracyInsightModalVisible && literacyInsightModalInfo && (
        <LiteracyInsightModal
          literacyInsightModalInfo={literacyInsightModalInfo}
        />
      )}
    </div>
  );
};
